import React, { useEffect } from "react";
import { FiCheckCircle, FiAlertCircle, FiX } from "react-icons/fi";

const AUTO_DISMISS_MS = 4000;

/**
 * Transient status message pinned to the bottom-right of the viewport.
 * Renders nothing when `toast` is null; auto-dismisses after 4s.
 *
 * @param {{ toast: { id: number, type: "success"|"error", message: string } | null, onDismiss: () => void }} props
 */
const Toast = ({ toast, onDismiss }) => {
  useEffect(() => {
    if (!toast) return undefined;
    const timer = setTimeout(onDismiss, AUTO_DISMISS_MS);
    return () => clearTimeout(timer);
    // `toast.id` changes on every new toast, restarting the timer.
  }, [toast, onDismiss]);

  if (!toast) return null;

  const isError = toast.type === "error";
  const Icon = isError ? FiAlertCircle : FiCheckCircle;

  return (
    <div style={wrapperStyle} role="status" aria-live="polite">
      <div style={{ ...toastStyle, ...(isError ? errorToastStyle : successToastStyle) }}>
        <Icon aria-hidden="true" style={{ flexShrink: 0, fontSize: 18 }} />
        <span style={{ flex: 1 }}>{toast.message}</span>
        <button type="button" onClick={onDismiss} style={dismissStyle} aria-label="Dismiss">
          <FiX aria-hidden="true" />
        </button>
      </div>
    </div>
  );
};

const wrapperStyle = {
  position: "fixed",
  right: "24px",
  bottom: "24px",
  zIndex: 1000,
  maxWidth: "min(420px, calc(100vw - 48px))",
};

const toastStyle = {
  display: "flex",
  alignItems: "center",
  gap: "12px",
  padding: "14px 16px",
  borderRadius: "8px",
  fontSize: "14px",
  fontWeight: "600",
  boxShadow: "0 8px 24px rgba(15, 23, 42, 0.18)",
  border: "1px solid",
};

const successToastStyle = {
  backgroundColor: "#d1fae5",
  borderColor: "#10b981",
  color: "#047857",
};

const errorToastStyle = {
  backgroundColor: "#fee2e2",
  borderColor: "#ef4444",
  color: "#b91c1c",
};

const dismissStyle = {
  display: "inline-flex",
  alignItems: "center",
  justifyContent: "center",
  background: "transparent",
  border: "none",
  color: "inherit",
  cursor: "pointer",
  padding: 0,
  fontSize: "16px",
  opacity: 0.7,
};

export default Toast;
